import React, { useState, useEffect, useRef, useMemo } from "react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import { Step, StepLabel, Stepper } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEPMContext } from "../../../contexts/EPMContext";
import { useUserContext } from "../../../contexts/UserContext";
import { permission } from "../../../config/permission";
import WorkflowStep from "./WorkflowStep";
import StepGateNextbar from "../validation/StepGateNextbar";
import { getStepCompleteness } from "../validation/stepCompleteness";
import "./styles.css";
import { formatTestCardId } from "../../../utils/converter";
import { STATUS_AFTER_STEP, hasReachedStep, laterStatus, ownerInitials, ownerName, stepForStatus } from "../epmHelpers";

// How many steps a test has finished, by status (ticks in the stepper)
const STEPS_DONE = { IDEAS: 0, PRIORITISED: 1, PLANNING: 2, BUILD_QA: 3, SCHEDULED: 4, LIVE: 5, ANALYSING: 5, CONCLUDED: 6 };

// Footer action per step; it is the one action that moves the test forward
const NEXT_LABELS = ["Send to prioritisation", "Accept — Plan", "Send to build", "Mark ready", "Mark as live", "Conclude test"];

const GATE_TITLES = [
    "Ready to prioritise",
    "Ready to plan",
    "Ready to build",
    "Ready to schedule",
    "Ready to go live",
    "Ready to conclude",
];

const steps = [
    {
        label: "Idea",
        description: "Define the opportunity and expected customer impact.",
    },
    {
        label: "Priority",
        description: "Score the idea and confirm its programme priority.",
    },
    {
        label: "Plan",
        description: "Set the hypothesis, audience, metrics, and guardrails.",
    },
    {
        label: "Build & QA",
        description: "Prepare the experience and complete quality checks.",
    },
    {
        label: "Schedule",
        description: "Choose the launch window and resolve lane conflicts.",
    },
    {
        label: "Result",
        description: "Review the outcome and record your learning.",
    },
];

const TestWorkflow = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const {
        tests,
        fetchTestById,
        updateTest,
        testLocations = [],
        stagingChecklist = [],
        productionChecklist = [],
        testChecklistsCache = {},
    } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);

    const [activeStep, setActiveStep] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const isLastStep = activeStep === steps.length - 1;
    const hasPlacedStep = useRef(false);
    // Saves the Results step's learning now (it auto-saves); set while that step is open
    const learningFlushRef = useRef(null);
    const stepGateRef = useRef(null);

    const [testData, setTestData] = useState(() => {
        return (tests || []).find((t) => String(t._id || t.id) === id) || null;
    });

    const stepsDone = STEPS_DONE[testData?.status] ?? 0;
    const stepIsDone = activeStep < stepsDone;
    // A step the test hasn't reached shows a placeholder, so its action is off
    const stepNotReached = !!testData && activeStep >= 3 && !hasReachedStep(testData, activeStep);

    const authorNameStr = ownerName(testData);
    const initials = ownerInitials(testData);

    const oneOffChecklists = (testData?._id || testData?.id)
        ? testChecklistsCache[String(testData._id || testData.id)] || []
        : [];

    const completeness = useMemo(() => {
        return getStepCompleteness(activeStep, testData, {
            testLocations,
            stagingChecklist,
            productionChecklist,
            oneOffChecklists,
            tests,
        });
    }, [activeStep, testData, testLocations, stagingChecklist, productionChecklist, oneOffChecklists, tests]);

    useEffect(() => {
        const fetchTest = async () => {
            if (!id || !fetchTestById) return;
            try {
                const data = await fetchTestById(id);
                if (data) setTestData(data);
            } catch (err) {
                console.error("Error fetching test details:", err);
            }
        };

        fetchTest();
    }, [id, fetchTestById]);

    // Open at ?step=N, otherwise at the step the test has reached (storyboard 10)
    useEffect(() => {
        if (hasPlacedStep.current || !testData) return;
        hasPlacedStep.current = true;
        const requested = Number(searchParams.get("step"));
        const step = Number.isInteger(requested) && requested >= 0 && requested < steps.length
            ? requested
            : stepForStatus(testData.status);
        setActiveStep(step);
    }, [testData, searchParams]);

    const saveTestDraft = async (customPayload = {}) => {
        if (!canUpdate) return null;
        const testId = testData?._id || testData?.id || id;
        if (!testId || !updateTest) return;

        setIsSaving(true);
        try {
            const payload = {
                name: testData?.name,
                description: testData?.description,
                status: testData?.status,
                priority: testData?.priority,
                triaged: testData?.triaged,
                ideation: testData?.ideation,
                schedule: testData?.schedule,
                plans: testData?.plans,
                verdict: testData?.verdict,
                result_link: testData?.result_link,
                tag_ids: testData?.tag_ids,
                ...customPayload,
            };
            const res = await updateTest(testId, payload);
            if (res) {
                setTestData((prev) => ({ ...prev, ...payload }));
                return res;
            }
        } catch (err) {
            console.error("Failed to save test draft:", err);
            toast.error(err?.message || "Couldn't save the test");
        } finally {
            setIsSaving(false);
        }
    };

    const handleNext = async () => {
        if (!canUpdate) {
            if (!isLastStep) {
                setActiveStep((step) => step + 1);
            } else {
                navigate("/epm/testing-board");
            }
            return;
        }

        // Completeness gate: if advancing this step forward, verify completeness
        if (!stepIsDone) {
            if (!completeness.isComplete) {
                const firstUnmet = stepGateRef.current?.openAndScrollToFirstError();
                toast.error(
                    firstUnmet?.label
                        ? `Please complete: ${firstUnmet.label}`
                        : "Please complete all required fields for this step before moving forward."
                );
                return;
            }
        }

        const extra = {};
        // The footer action on the Priority step is "Accept", even after a Park
        if (activeStep === 1) {
            extra.triaged = { ...testData?.triaged, status: "ACCEPT" };
        }
        // Taking a step's action moves the test on the board, never backwards
        const target = STATUS_AFTER_STEP[activeStep];
        if (target) {
            extra.status = laterStatus(testData?.status, target);
        }
        // Concluding records the learning even if it was never edited
        if (isLastStep && (await learningFlushRef.current?.({ force: true })) === false) return;
        const saved = await saveTestDraft(extra);
        if (!saved) return;
        if (!isLastStep) {
            setActiveStep((step) => step + 1);
        } else {
            navigate("/epm/testing-board");
        }
    };

    const handleBack = async () => {
        if (activeStep > 0) {
            setActiveStep((step) => step - 1);
        }
    };

    const handleStepClick = async (index) => {
        if (canUpdate) {
            await saveTestDraft();
        }
        setActiveStep(index);
    };

    const handleSaveAndExit = async () => {
        if (canUpdate) {
            if ((await learningFlushRef.current?.({ force: true })) === false) return;
            await saveTestDraft();
        }
        navigate("/epm/testing-board");
    };

    return (
        <main className="epm-workflow">
            <header className="epm-workflow__header">
                <div>
                    <p className="epm-workflow__eyebrow">
                        {formatTestCardId(testData || id)}
                    </p>
                    <h1>{testData?.name || "Untitled Test"}</h1>
                    <p className="epm-workflow__author flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-semibold flex-none">
                            {initials}
                        </span>
                        {authorNameStr || "Unknown"}
                    </p>
                </div>
                <button
                    className="epm-workflow__close"
                    type="button"
                    onClick={handleSaveAndExit}
                    disabled={isSaving}
                >
                    {canUpdate ? "Save and exit" : "Exit"}
                </button>
            </header>

            <section className="epm-workflow__shell">
                <nav
                    className="epm-workflow__stepper"
                    aria-label="Test creation progress"
                >
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((step, index) => (
                            <Step
                                key={step.label}
                                completed={index < stepsDone}
                            >
                                <StepLabel
                                    onClick={() => handleStepClick(index)}
                                >
                                    {step.label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </nav>

                <div className={`epm-workflow__content ${!canUpdate ? 'epm-workflow__content--readonly' : ''}`}>
                    <div className="epm-workflow__progress-copy">
                        Step {activeStep + 1} of {steps.length}
                    </div>
                    <WorkflowStep
                        step={steps[activeStep]}
                        stepIndex={activeStep}
                        testData={testData}
                        setTestData={setTestData}
                        learningFlushRef={learningFlushRef}
                    />

                    {!stepNotReached && (
                        <StepGateNextbar
                            ref={stepGateRef}
                            title={GATE_TITLES[activeStep]}
                            completeness={completeness}
                        />
                    )}
                </div>

                <footer className="epm-workflow__footer">
                    <button
                        className="epm-workflow__secondary"
                        type="button"
                        disabled={activeStep === 0 || isSaving}
                        onClick={handleBack}
                    >
                        <ChevronLeft size={17} /> Back
                    </button>
                    <span>
                        {!canUpdate
                            ? "View-only mode"
                            : isSaving
                            ? "Saving changes..."
                            : "Changes are saved as a draft"}
                    </span>
                    <button
                        className="epm-workflow__primary"
                        type="button"
                        disabled={canUpdate ? (isSaving || stepNotReached) : false}
                        title={canUpdate && stepNotReached ? "This test hasn't reached this step yet" : undefined}
                        onClick={handleNext}
                    >
                        {!canUpdate ? (
                            isLastStep ? (
                                <>Back to board <ChevronRight size={17} /></>
                            ) : (
                                <>Next <ChevronRight size={17} /></>
                            )
                        ) : isLastStep && stepIsDone ? (
                            <>
                                Back to board <ChevronRight size={17} />
                            </>
                        ) : isLastStep ? (
                            <>
                                <Check size={17} /> Conclude test
                            </>
                        ) : (
                            <>
                                {stepIsDone ? "Next" : NEXT_LABELS[activeStep]} <ChevronRight size={17} />
                            </>
                        )}
                    </button>
                </footer>
            </section>
        </main>
    );
};

export default TestWorkflow;
